import { LitElement, html, nothing, type PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { t } from '@vitesse/base-ui/i18n';
import { SearchAutocomplete } from '../../shell/symbolGrid/search/SearchAutocomplete';
import { formatSearchInput } from '../../shell/symbolGrid/search/searchCommands';
import type { BackendClient } from '../../bridge/backend-client';
import '@vitesse/base-ui/elements/vitesse-dialog';
import '@vitesse/base-ui/elements/vitesse-icon';

/*
 * <vitesse-history-symbol-picker> — "add a lane" for the History tab: pick ONE
 * symbol from the controller's catalog. A single-pane copy of the proven
 * model-bindings picker (same server-paged virtual list, same search chrome,
 * same `/` command grammar) minus the source tree — the controller is already
 * fixed by the page — and minus staging: a lane add is cheap and instantly
 * reversible (the chip's ×), so a click commits.
 *
 * SERVER-PAGED, like the bindings picker: the indexer linearizes and answers
 * `offset`, so a 50k-symbol controller costs the same as a 50-symbol one — a
 * window of rows plus `totalMatching` for the scrollbar. Branch rows render
 * dimmed and inert rather than filtered out (client-side filtering would
 * desynchronise our offsets from the indexer's). Already-charted symbols
 * (`taken`) render selected-and-inert, so the cap reads at the point of use.
 *
 * Emits `symbol-pick` (detail: the plain symbol path) on a leaf click; the
 * dialog's own `dialog-close` bubbles through for the owner to clear `open`.
 */

/** Rows per fetched window — wide enough that the viewport sits deep inside it. */
const WINDOW = 400;
/** Scroll distance (rows) between refetches — quantizes a drag to strides. */
const STRIDE = 150;
/** Must match .hsp-vrow's height (the grid's --sg-row-height). */
const ROW_H = 34;
/** The rows pane's height (see .hsp-rows) — drives how many rows we render. */
const VIEWPORT_H = 420;

type SymRow = { path: string; dataType: string; leaf: boolean };

@customElement('vitesse-history-symbol-picker')
export class HistorySymbolPicker extends LitElement {
  @property({ attribute: false }) backend?: BackendClient;
  @property() gatewayId = '';
  @property() controllerId = '';
  @property({ type: Boolean }) open = false;
  /** Paths already charted — rendered inert so the pick can't duplicate. */
  @property({ attribute: false }) taken: string[] = [];

  @state() private query = '';
  @state() private rows: SymRow[] = [];
  @state() private loading = false;
  /** Rows in `rows` start at this index of the FULL match set. */
  @state() private winStart = 0;
  /** How many rows the indexer says match — the scrollbar's truth. */
  @state() private total = 0;
  @state() private scrollY = 0;
  private searchTimer?: ReturnType<typeof setTimeout>;
  private fetchSeq = 0;
  /** Offset of the window currently in flight (-1 = none) — dedups a drag. */
  private pending = -1;
  /** The `/` command hints — the SAME autocomplete the symbol grid uses. */
  private ac: SearchAutocomplete | null = null;

  protected createRenderRoot(): HTMLElement {
    return this; // light DOM: reuses the grid's global .sg-search-* chrome
  }

  protected willUpdate(changed: PropertyValues): void {
    if (changed.has('open') && this.open) {
      // Fresh picker per open — a stale window from last time would flash.
      this.query = '';
      this.resetWindow();
      void this.loadSymbols(0);
    }
  }

  protected updated(): void {
    this.syncAutocomplete();
  }

  disconnectedCallback(): void {
    this.ac?.destroy();
    this.ac = null;
    clearTimeout(this.searchTimer);
    super.disconnectedCallback();
  }

  private resetWindow(): void {
    this.pending = -1;
    this.rows = [];
    this.winStart = 0;
    this.total = 0;
    this.scrollY = 0;
    const el = this.querySelector('.hsp-rows');
    if (el) el.scrollTop = 0;
  }

  private debouncedLoad(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => void this.loadSymbols(0), 250);
  }

  /** Fetch ONE WINDOW of the match set — never the whole catalog. */
  private async loadSymbols(offset: number): Promise<void> {
    if (!this.backend || !this.gatewayId || !this.controllerId) {
      this.rows = [];
      this.total = 0;
      return;
    }
    const seq = ++this.fetchSeq;
    this.loading = this.total === 0; // only the FIRST paint is a "loading"; scrolling must not flash
    try {
      const start = Math.max(0, offset);
      const page = await this.backend.api.fetchSymbolPage(this.gatewayId, this.controllerId, {
        fts: this.query,
        offset: start,
        pageSize: WINDOW,
      });
      if (seq !== this.fetchSeq) return; // a newer query/window won; drop this one
      this.winStart = start;
      this.rows = page.rows.map((r) => ({ path: r.path, dataType: r.dataType, leaf: r.childCount === 0 }));
      this.total = page.totalMatching || page.rows.length;
    } catch {
      if (seq !== this.fetchSeq) return;
      this.rows = [];
      this.total = 0;
    } finally {
      if (seq === this.fetchSeq) {
        this.loading = false;
        this.pending = -1;
      }
    }
  }

  /** The window offset we WANT for a given first-visible row — centred on the
   *  viewport and quantized to a stride (a drag costs one query per stride
   *  crossed, not one per scroll event). */
  private windowFor(first: number): number {
    const centred = first - Math.floor((WINDOW - VIEWPORT_H / ROW_H) / 2);
    const quantized = Math.floor(Math.max(0, centred) / STRIDE) * STRIDE;
    return Math.max(0, Math.min(quantized, Math.max(0, this.total - WINDOW)));
  }

  private onScroll(e: Event): void {
    const el = e.target as HTMLElement;
    this.scrollY = el.scrollTop;
    const want = this.windowFor(Math.floor(el.scrollTop / ROW_H));
    if (want === this.winStart || want === this.pending) return;
    this.pending = want;
    void this.loadSymbols(want);
  }

  // ── the `/` hints (the grid's own SearchAutocomplete, dataless on purpose:
  //    this view is server-paged, so command/enum suggestions must be the
  //    declared full set, never "the types among the rows I hold") ────────────
  private syncAutocomplete(): void {
    const wrap = this.querySelector('.hsp-bar .sg-search-wrap') as HTMLElement | null;
    if (!wrap) {
      this.ac?.destroy();
      this.ac = null;
      return;
    }
    if (this.ac) return;
    this.ac = new SearchAutocomplete(wrap, {
      getSymbolData: () => null,
      onSelect: (completion: string) => {
        const input = this.querySelector('.hsp-search-input') as HTMLInputElement | null;
        if (!input) return;
        const at = input.value.lastIndexOf('/'); // replace from the last '/' — the grid's splice
        input.value = (at >= 0 ? input.value.slice(0, at) : input.value) + completion;
        this.query = input.value;
        this.resetWindow();
        void this.loadSymbols(0);
        input.focus();
      },
      onClose: () => {},
    });
  }

  private onSearchInput(e: InputEvent): void {
    const input = e.target as HTMLInputElement;
    this.query = input.value;
    this.ac?.update(input.value, input.selectionStart ?? input.value.length, input.getBoundingClientRect());
    this.debouncedLoad();
  }

  /** mousedown, not click — it must beat the input's blur (the grid's own trick). */
  private onSearchClear(e: MouseEvent): void {
    e.preventDefault();
    this.query = '';
    this.ac?.hide();
    this.resetWindow();
    void this.loadSymbols(0);
    const input = this.querySelector('.hsp-search-input') as HTMLInputElement | null;
    if (input) {
      input.value = '';
      input.focus();
    }
  }

  private onSearchKey(e: KeyboardEvent): void {
    // while the popup is open it owns arrows / enter / escape
    if (this.ac?.handleKeyDown(e)) e.preventDefault();
  }

  private pick(path: string): void {
    this.dispatchEvent(new CustomEvent<string>('symbol-pick', { detail: path }));
  }

  // ── render ─────────────────────────────────────────────────────────────────

  private renderVirtual() {
    if (this.loading && this.rows.length === 0) return html`<div class="hsp-hint">${t('common.loading')}</div>`;
    if (this.total === 0) return html`<div class="hsp-hint">${t('model.noSymbols')}</div>`;
    const first = Math.max(0, Math.floor(this.scrollY / ROW_H) - 4);
    const last = Math.min(this.total, first + Math.ceil(VIEWPORT_H / ROW_H) + 8);
    const out = [];
    for (let i = first; i < last; i++) {
      const r = this.rows[i - this.winStart];
      out.push(
        r
          ? this.renderRow(r, i)
          : // in the gap between windows — hold the row's space so nothing jumps
            html`<div class="hsp-vrow hsp-vrow--ghost" style="transform:translateY(${i * ROW_H}px)"></div>`,
      );
    }
    return html`<div class="hsp-spacer" style="height:${this.total * ROW_H}px">${out}</div>`;
  }

  private renderRow(r: SymRow, i: number) {
    if (!r.leaf) {
      // a branch: context, not a value — visible so the tree still reads, inert
      return html`<div class="hsp-vrow hsp-vrow--branch" style="transform:translateY(${i * ROW_H}px)">
        <span class="hsp-vname">${r.path}</span>
      </div>`;
    }
    if (this.taken.includes(r.path)) {
      return html`<div class="hsp-vrow hsp-vrow--taken sel" style="transform:translateY(${i * ROW_H}px)">
        <span class="hsp-vname">${r.path}</span>
        <vitesse-icon name="check" .size=${13}></vitesse-icon>
      </div>`;
    }
    return html`<button class="hsp-vrow" style="transform:translateY(${i * ROW_H}px)" @click=${() => this.pick(r.path)}>
      <span class="hsp-vname">${r.path}</span>
      <span class="hsp-vtype">${r.dataType}</span>
    </button>`;
  }

  render() {
    return html`${this.styles()}
      <vitesse-dialog width="560px" .open=${this.open} heading=${t('controller.historyPickerTitle')}>
        ${!this.open
          ? nothing
          : html`<div class="hsp-view">
              <div class="sg-toolbar hsp-bar">
                <div class="sg-search-wrap">
                  <vitesse-icon class="sg-search-icon" name="search" .size=${14}></vitesse-icon>
                  <div class="sg-search-highlight">
                    ${formatSearchInput(this.query).map((tok) => html`<span class="sg-token-${tok.type}">${tok.text}</span>`)}
                  </div>
                  <input
                    class="sg-search-input hsp-search-input"
                    .value=${this.query}
                    spellcheck="false"
                    autocomplete="off"
                    placeholder=${t('grid.searchPlaceholder')}
                    @input=${(e: InputEvent) => this.onSearchInput(e)}
                    @keydown=${(e: KeyboardEvent) => this.onSearchKey(e)}
                    @focus=${(e: FocusEvent) => {
                      const el = e.target as HTMLInputElement;
                      this.ac?.update(el.value, el.selectionStart ?? el.value.length, el.getBoundingClientRect());
                    }}
                    @blur=${() => setTimeout(() => this.ac?.hide(), 150)}
                  />
                  ${this.query
                    ? html`<vitesse-icon
                        class="sg-search-clear"
                        name="x"
                        .size=${14}
                        @mousedown=${(e: MouseEvent) => this.onSearchClear(e)}
                      ></vitesse-icon>`
                    : nothing}
                </div>
              </div>
              <div class="hsp-rows" @scroll=${(e: Event) => this.onScroll(e)}>${this.renderVirtual()}</div>
            </div>`}
      </vitesse-dialog>`;
  }

  /** Light-DOM styles, scoped by the element tag — the bindings picker's row
   *  system (the grid's metrics + hairline, the tree's blue pick-hover). */
  private styles() {
    return html`<style>
      vitesse-history-symbol-picker .hsp-view {
        display: flex;
        flex-direction: column;
        height: min(52vh, ${VIEWPORT_H}px);
        background: var(--sg-bg);
        border: 1px solid var(--sg-border-subtle);
        border-radius: 6px;
        overflow: hidden;
      }
      vitesse-history-symbol-picker .hsp-bar {
        flex-shrink: 0;
      }
      vitesse-history-symbol-picker .hsp-bar .sg-search-icon {
        color: var(--text-secondary);
        display: flex;
        align-items: center;
      }
      vitesse-history-symbol-picker .hsp-rows {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
      }
      vitesse-history-symbol-picker .hsp-rows::-webkit-scrollbar {
        width: 6px;
      }
      vitesse-history-symbol-picker .hsp-rows::-webkit-scrollbar-track {
        background: transparent;
      }
      vitesse-history-symbol-picker .hsp-rows::-webkit-scrollbar-thumb {
        background: var(--sg-border-subtle);
        border-radius: 3px;
      }
      vitesse-history-symbol-picker .hsp-spacer {
        position: relative;
        width: 100%;
      }
      vitesse-history-symbol-picker .hsp-vrow {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        height: var(--sg-row-height, ${ROW_H}px);
        padding: 0 12px;
        border: none;
        border-bottom: 1px solid var(--sg-border-subtle);
        background: transparent;
        color: var(--text-primary);
        font: inherit;
        font-size: 12px;
        text-align: left;
        cursor: pointer;
        transition: background 0.1s ease;
        position: absolute;
        top: 0;
        left: 0;
        box-sizing: border-box;
      }
      vitesse-history-symbol-picker .hsp-vrow:hover {
        background: rgb(from var(--blue) r g b / 0.12);
      }
      vitesse-history-symbol-picker .hsp-vrow--branch {
        color: var(--text-secondary);
        opacity: 0.55;
        cursor: default;
      }
      vitesse-history-symbol-picker .hsp-vrow--branch:hover {
        background: transparent;
      }
      vitesse-history-symbol-picker .hsp-vrow--ghost {
        cursor: default;
      }
      vitesse-history-symbol-picker .hsp-vrow--ghost:hover {
        background: transparent;
      }
      /* already a lane: shown as picked, not pickable again */
      vitesse-history-symbol-picker .hsp-vrow--taken {
        cursor: default;
      }
      vitesse-history-symbol-picker .hsp-vrow.sel {
        background: rgb(from var(--blue) r g b / 0.18);
        color: var(--blue);
      }
      vitesse-history-symbol-picker .hsp-vrow.sel:hover {
        background: rgb(from var(--blue) r g b / 0.18);
      }
      vitesse-history-symbol-picker .hsp-vname {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-family: ui-monospace, 'SF Mono', monospace;
      }
      vitesse-history-symbol-picker .hsp-vtype {
        flex-shrink: 0;
        color: var(--text-secondary);
        font-size: 11px;
        font-family: ui-monospace, 'SF Mono', monospace;
      }
      vitesse-history-symbol-picker .hsp-hint {
        font-size: 12px;
        color: var(--text-secondary);
        opacity: 0.7;
        padding: 10px 12px;
      }
    </style>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-history-symbol-picker': HistorySymbolPicker;
  }
}
